import React, { useState, useEffect, useMemo } from "react";
import { loadStripe } from "@stripe/stripe-js";
import { Elements } from "@stripe/react-stripe-js";
import { StripePaymentForm } from "./StripePaymentForm";
import { Card } from "./ui/Card";
import { Stack } from "./ui/Stack";
import { Heading } from "./ui/Heading";
import { Button } from "./ui/Button";
import { Alert } from "./ui/Alert";
import { Text } from "./ui";

interface CheckoutPaymentProps {
 clientSecret: string | null;
 amount: number;
 currency?: string;
 stripePublishableKey: string | null;
 isStripeConfigured: boolean;
 paypalClientId?: string | null;
 isPayPalConfigured?: boolean;
 selectedGateway?: "stripe" | "paypal" | null;
 onGatewaySelect: (gateway: "stripe" | "paypal") => void;
 onPaymentSuccess: (paymentIntentId: string) => void;
 onBack: () => void;
 onError: (error: string) => void;
}

export const CheckoutPayment: React.FC<CheckoutPaymentProps> = ({
 clientSecret,
 amount,
 currency = "USD",
 stripePublishableKey,
 isStripeConfigured,
 paypalClientId,
 isPayPalConfigured = false,
 selectedGateway = null,
 onGatewaySelect,
 onPaymentSuccess,
 onBack,
 onError,
}) => {
 const [error, setError] = useState<string | null>(null);

 // Create Stripe promise with store's publishable key
 const stripePromise = useMemo(() => {
  if (!stripePublishableKey) {
   return null;
  }
  return loadStripe(stripePublishableKey);
 }, [stripePublishableKey]);

 const isPayPal = selectedGateway === "paypal";
 const isStripe = selectedGateway === "stripe";

 // These hooks must be called before any early returns
 useEffect(() => {
  if (isPayPal && (!isPayPalConfigured || !paypalClientId)) {
   setError("PayPal payment is not available. Please contact the store administrator to configure PayPal settings.");
   onError("PayPal integration is not configured for this location");
  } else if (isStripe && (!isStripeConfigured || !stripePublishableKey)) {
   setError("Credit card payment is not available. Please contact the store administrator to configure payment settings.");
   onError("Stripe integration is not configured for this location");
  } else if (selectedGateway && !clientSecret) {
   // Only show error if gateway is selected but no client secret yet
   // This is normal while payment intent is being created
  }
 }, [clientSecret, onError, isStripeConfigured, stripePublishableKey, isPayPalConfigured, paypalClientId, isPayPal, isStripe, selectedGateway]);

 // For PayPal, the clientSecret contains the approval URL - redirect to PayPal for approval
 useEffect(() => {
  if (isPayPal && clientSecret && isPayPalConfigured && paypalClientId) {
   try {
    const paypalData = typeof clientSecret === "string" ? JSON.parse(clientSecret) : clientSecret;
    const approveUrl = paypalData?.approveUrl || paypalData?.links?.find((link: any) => link.rel === "approve")?.href;
    if (approveUrl) {
     window.location.href = approveUrl;
    } else {
     setError("Failed to get PayPal approval URL. Please try again.");
     onError("Failed to get PayPal approval URL");
    }
   } catch (err) {
    setError("Failed to initialize PayPal payment. Please try again.");
    onError("Failed to parse PayPal response");
   }
  }
 }, [isPayPal, clientSecret, isPayPalConfigured, paypalClientId, onError]);

 const hasAnyGateway = isStripeConfigured || isPayPalConfigured;

 if (!hasAnyGateway) {
  return (
   <Card padding="lg">
    <Stack spacing="md">
     <Heading size="lg">Payment</Heading>
     <Alert variant="error">
      Online payment is not available. Please contact the store administrator to configure payment settings.
     </Alert>
     <Button variant="outline" onClick={onBack}>
      Back
     </Button>
    </Stack>
   </Card>
  );
 }

 return (
  <Card padding="lg">
   <Stack spacing="md">
    <Heading size="lg">Payment</Heading>
    <Text className="text-gray-600">
     Select your preferred payment method
    </Text>

    {error && <Alert variant="error">{error}</Alert>}

    <div className="space-y-3 mt-4">
     {/* Stripe Option */}
     {isStripeConfigured && (
      <div className="border-2 rounded-lg overflow-hidden"
           style={{
            borderColor: selectedGateway === "stripe" ? "#3b82f6" : "#e5e7eb"
           }}>
       <div
        onClick={() => selectedGateway !== "stripe" && onGatewaySelect("stripe")}
        className="p-4 cursor-pointer transition-all hover:bg-gray-50"
       >
        <div className="flex items-center gap-3">
         <input
          type="radio"
          name="paymentGateway"
          id="stripe"
          checked={selectedGateway === "stripe"}
          onChange={() => onGatewaySelect("stripe")}
          className="w-4 h-4 text-blue-600"
         />
         <label htmlFor="stripe" className="cursor-pointer flex-1">
          <Text weight="semibold" className="mb-1">
           Pay with Credit Card
          </Text>
          <Text size="sm" className="text-gray-600">
           Pay securely with your credit or debit card
          </Text>
         </label>
        </div>
       </div>
       
       {/* Stripe Payment Form - Expanded when selected */}
       {selectedGateway === "stripe" && (
        <div className="border-t border-gray-200 p-4 bg-gray-50">
         {!stripePublishableKey ? (
          <Alert variant="error">
           <Text size="sm">Credit card payment is not available. Please contact the store administrator.</Text>
          </Alert>
         ) : !clientSecret ? (
          <div className="py-4">
           <Text size="sm" className="text-gray-600">Initializing payment...</Text>
          </div>
         ) : !stripePromise ? (
          <Alert variant="error">
           <Text size="sm">Failed to initialize payment processor. Please try again.</Text>
          </Alert>
         ) : (
          <Elements stripe={stripePromise} options={{ clientSecret }}>
           <StripePaymentForm
            clientSecret={clientSecret}
            amount={amount}
            currency={currency}
            onSuccess={onPaymentSuccess}
            onError={(err) => {
             setError(err);
             onError(err);
            }}
           />
          </Elements>
         )}
        </div>
       )}
      </div>
     )}

     {/* PayPal Option */}
     {isPayPalConfigured && (
      <div className="border-2 rounded-lg overflow-hidden"
           style={{
            borderColor: selectedGateway === "paypal" ? "#3b82f6" : "#e5e7eb"
           }}>
       <div
        onClick={() => selectedGateway !== "paypal" && onGatewaySelect("paypal")}
        className="p-4 cursor-pointer transition-all hover:bg-gray-50"
       >
        <div className="flex items-center gap-3">
         <input
          type="radio"
          name="paymentGateway"
          id="paypal"
          checked={selectedGateway === "paypal"}
          onChange={() => onGatewaySelect("paypal")}
          className="w-4 h-4 text-blue-600"
         />
         <label htmlFor="paypal" className="cursor-pointer flex-1">
          <Text weight="semibold" className="mb-1">
           PayPal
          </Text>
          <Text size="sm" className="text-gray-600">
           Pay securely with your PayPal account
          </Text>
         </label>
        </div>
       </div>

       {/* PayPal Payment UI - Expanded when selected */}
       {selectedGateway === "paypal" && (
        <div className="border-t border-gray-200 p-4 bg-gray-50">
         {!paypalClientId ? (
          <Alert variant="error">
           <Text size="sm">PayPal payment is not available. Please contact the store administrator.</Text>
          </Alert>
         ) : !clientSecret ? (
          <div className="py-4">
           <Text size="sm" className="text-gray-600">Initializing PayPal payment...</Text>
          </div>
         ) : (
          <div className="py-4">
           <Text size="sm" className="text-gray-600 mb-4">
            You will be redirected to PayPal to complete your payment.
           </Text>
           <div className="py-2">
            <Text size="xs" className="text-gray-500 italic">Redirecting to PayPal...</Text>
           </div>
          </div>
         )}
        </div>
       )}
      </div>
     )}
    </div>

    <Button variant="outline" onClick={onBack} type="button" className="mt-4">
     Back
    </Button>
   </Stack>
  </Card>
 );
};

